import { useStore } from "@/stores/useStore.ts";
import { Link, useParams } from "react-router-dom";
import { useToast } from "@/hooks/useToast.tsx";
import { LoadingPage } from "@/components/page/LoadingPage.tsx";
import { CreateCustomerDto, Customer, PatchCustomerDto } from "@/models";
import { customer as customerActions } from "@/repositories/customer.ts";
import { generator as generatorActions } from "@/repositories/generator.ts";
import { PageHeading } from "@/components/common/PageHeading.tsx";
import { CreateEditCustomerForm } from "@/components/forms/CreateEditCustomer.form.tsx";
import { useEffect, useState } from "react";
import { Button } from "@/components/base/Button.tsx";
import { ArrowLeftIcon, PencilIcon } from "lucide-react";
import { cn } from "@/lib/utils.ts";

export const CustomerDetailPage = () => {
  const tenant = useStore((s) => s.tenant);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [customer, setCustomer] = useState<Customer>();
  const { customerId } = useParams();
  const { toast } = useToast();

  useEffect(() => {
    if (!customerId || !tenant) {
      return;
    }
    const unsubsribe = customerActions.subscribe(
      customerId,
      (doc) => setCustomer(doc),
      tenant.id
    );
    return unsubsribe;
  }, [customerId, tenant]);

  if (!tenant || !customer) {
    return <LoadingPage />;
  }

  const handleOnSubmit = async (
    values: CreateCustomerDto | PatchCustomerDto
  ) => {
    if (!(values as PatchCustomerDto).id) {
      return;
    }
    if (!customerId) {
      return;
    }
    if (values.generatorId) {
      if (
        values.generatorId !== customer.generatorId &&
        customer.generatorId !== ""
      ) {
        await generatorActions.update(
          customer.generatorId,
          { customerId: "" },
          tenant.id
        );
      }
      await generatorActions.update(
        values.generatorId,
        { customerId: customerId },
        tenant.id
      );
    }
    await customerActions
      .update(customerId, values as PatchCustomerDto, tenant.id)
      .then((c) => {
        toast({
          title: "Customer Updated!",
          description: `${c.firstName} ${c.lastName} updated successfully.`,
        });
      })
      .finally(() => setIsEditing(false));

    return;
  };

  const handleCancel = async () => {
    setIsEditing(false);
  };

  return (
    <div className="p-4 w-full">
      <div className="flex flex-row space-between pb-4">
        <PageHeading>Customer Details</PageHeading>
        <div className="flex flex-grow" />
        <Link to="..">
          <Button>
            <ArrowLeftIcon /> Back to Customers
          </Button>
        </Link>
      </div>

      <div className="flex flex-row space-between pb-4">
        <div className="flex flex-grow" />
        <Button
          onClick={() => setIsEditing(!isEditing)}
          className={cn(isEditing ? "invisible" : "visible")}
        >
          <PencilIcon />
        </Button>
      </div>
      <CreateEditCustomerForm
        onSubmit={handleOnSubmit}
        tenantId={tenant.id}
        onCancel={handleCancel}
        initialCustomerData={customer}
        isEditable={isEditing}
      />
    </div>
  );
};
